import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../../store/user.store';
import { useBatches, useUpdateBatchStatus } from '../../../hooks/useBatches';
import { useBatchStore } from '../../../store/batch.store';
import { useShipments, useAddShipment, useRemoveShipment, useUpdateShipment } from '../../../hooks/useShipments';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Shipment } from '../../../services/shipment.service';
import { supabase } from '../../../services/supabase';
import { useLabelPrint } from '../../../hooks/useLabelPrint';
import { useNumberRules } from '../../../hooks/useNumberRules';
import { useScannerStore } from '../../../store/scanner.store';
import { SelectedReceiver } from './ContactPicker';

// Sub-components
// mini-app ui is extracted to components for better maintainability.
import CargoCreateHeader from './CargoCreateHeader';
import RecentRecords from './RecentRecords';
import CargoCreateForm from './CargoCreateForm';
import CargoCreateFooter from './CargoCreateFooter';
import FinalizeModal from './FinalizeModal';
import EditShipmentModal from './EditShipmentModal';
import DeleteConfirmDialog from './DeleteConfirmDialog';

const CargoCreate: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useUserStore();
    const { t } = useTranslation();
    const { data: batches, isLoading: batchesLoading } = useBatches();
    const { data: rules } = useNumberRules(user?.company_id);
    const { activeBatchId, setActiveBatchId } = useBatchStore();

    // State for the form
    const [waybillNo, setWaybillNo] = useState('');
    const [weight, setWeight] = useState('12.85');
    const [length, setLength] = useState('');
    const [width, setWidth] = useState('');
    const [height, setHeight] = useState('');
    const [shipperName, setShipperName] = useState('');
    const [receiver, setReceiver] = useState<SelectedReceiver | null>(null);
    const [transportMode, setTransportMode] = useState<number>(1);
    const [itemCategory, setItemCategory] = useState('');
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [editingShipment, setEditingShipment] = useState<Shipment | null>(null);
    const [deleteShipmentId, setDeleteShipmentId] = useState<string | null>(null);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    /** 最新建档的运单 — 触发悬浮倒计时卡片 */
    const [latestShipment, setLatestShipment] = useState<Shipment | null>(null);
    const [isGenChoiceModalOpen, setIsGenChoiceModalOpen] = useState(false);

    // Form states for editing
    const [editWaybill, setEditWaybill] = useState('');
    const [editWeight, setEditWeight] = useState('');
    const [editLength, setEditLength] = useState('');
    const [editWidth, setEditWidth] = useState('');
    const [editHeight, setEditHeight] = useState('');
    const [editShipperName, setEditShipperName] = useState('');
    const [editTransportMode, setEditTransportMode] = useState<number>(1);
    const [editItemCategory, setEditItemCategory] = useState('');

    // Fetch shipments for the current batch
    const { data: shipments, isLoading: shipmentsLoading } = useShipments(activeBatchId || '');
    const addShipment = useAddShipment();
    const removeShipment = useRemoveShipment();
    const updateShipment = useUpdateShipment();
    const updateBatchStatus = useUpdateBatchStatus();

    // Bluetooth label printing
    const { printShipmentLabel, isPrinting: isLabelPrinting, printerConnected } = useLabelPrint({ silent: false, feature: 'cargo_create' });

    // Settings
    const { reprintMode } = useScannerStore();

    // Auto-select first active batch if none selected
    useEffect(() => {
        if (!activeBatchId && batches && batches.length > 0) {
            const firstActive = batches.find(b => b.status === 'sender_processing' || b.status === 'draft');
            if (firstActive) setActiveBatchId(firstActive.id);
        }
    }, [batches, activeBatchId, setActiveBatchId]);

    const handleWaybillGen = useCallback(async (forcedStrategy?: 'SEQUENCE' | 'RANDOM_ALPHA') => {
        if (!user?.company_id) {
            toast.error(t('common.error_no_company', '未找到公司信息'));
            return;
        }

        const rule = rules?.find(r => r.type === 'tracking_no');
        const strategy = forcedStrategy || rule?.generation_strategy || 'SEQUENCE';

        if (strategy === 'MANUAL_ASK' && !forcedStrategy) {
            setIsGenChoiceModalOpen(true);
            return;
        }

        const loadingToast = toast.loading(t('common.loading', '正在生成...'));
        try {
            const { data, error } = await supabase.rpc('generate_next_number', {
                p_company_id: user.company_id,
                p_type: 'tracking_no',
                p_force_strategy: strategy
            });

            if (error) throw error;

            if (data) {
                setWaybillNo(data);
                setIsGenChoiceModalOpen(false);
                toast.success(t('cargo.gen_success', '单号获取成功'));
            }
        } catch (err: any) {
            console.error('[CargoCreate] Gen error:', err);
            toast.error(t('cargo.gen_failed', '生成失败') + ': ' + err.message);
        } finally {
            toast.dismiss(loadingToast);
        }
    }, [user?.company_id, rules, t]);

    // Handle Scan & Custom Events
    useEffect(() => {
        const handleScan = (e: any) => {
            setWaybillNo(e.detail);
            toast.success(`已扫描: ${e.detail}`);
        };

        const onGenEvent = () => handleWaybillGen();
        window.addEventListener('pda-scan', handleScan);
        window.addEventListener('generate-waybill', onGenEvent);
        return () => {
            window.removeEventListener('pda-scan', handleScan);
            window.removeEventListener('generate-waybill', onGenEvent);
        };
    }, [user, handleWaybillGen]);

    const generateRandomWaybill = () => {
        return 'WA' + Math.random().toString(36).substring(2, 10).toUpperCase();
    };

    const handleCreate = async (providedWaybillNo?: string | any) => {
        if (!activeBatchId) {
            toast.error('请先选择或创建一个批次');
            return null;
        }

        // Auto-generate is handled by backend service if empty
        let targetWaybillNo = typeof providedWaybillNo === 'string' ? providedWaybillNo : waybillNo;
        if (!targetWaybillNo) {
            toast.loading('正在自动分配运单号...', { duration: 1000 });
        }

        try {
            const created = await addShipment.mutateAsync({
                batch_id: activeBatchId,
                tracking_no: targetWaybillNo,
                weight: parseFloat(weight),
                length: length ? parseFloat(length) : null,
                width: width ? parseFloat(width) : null,
                height: height ? parseFloat(height) : null,
                shipper_name: shipperName || null,
                transport_mode: transportMode,
                item_category: itemCategory || null,
                // 收货人信息（来自地址簿，打印到面单）
                receiver_name: receiver?.name || null,
                receiver_phone: receiver?.phone || null,
                receiver_address: receiver?.address || null,
                receiver_city: receiver?.city || null,
            });

            // 触发悬浮倒计时卡片（用返回值；若 mutateAsync 返回 void，则依赖 shipments 的第一条）
            if (created) {
                setLatestShipment(created as unknown as Shipment);
            }

            // Clear form
            setWaybillNo('');
            setLength('');
            setWidth('');
            setHeight('');
            setShipperName('');
            setReceiver(null);
            setItemCategory('');
            setTransportMode(1);
            toast.success('建档成功');
            return (created as any)?.tracking_no || targetWaybillNo;
        } catch (err: any) {
            toast.error('创建失败: ' + err.message);
            return null;
        }
    };

    const handlePrint = async () => {
        let printWaybillNo = waybillNo;
        // If empty, it will be generated during handleCreate

        // Save current form data before handleCreate clears it
        const printData = {
            tracking_no: printWaybillNo,
            weight: parseFloat(weight) || 0,
            length: length ? parseFloat(length) : undefined,
            width: width ? parseFloat(width) : undefined,
            height: height ? parseFloat(height) : undefined,
            shipper_name: shipperName || undefined,
            receiver_name: receiver?.name || undefined,
            receiver_phone: receiver?.phone || undefined,
            receiver_address: [receiver?.city, receiver?.address].filter(Boolean).join(' ') || undefined,
            batch_no: activeBatch?.batch_no,
        };

        const createdWaybill = await handleCreate(printWaybillNo);

        if (createdWaybill) {
            // Print label via Bluetooth
            // IMPORTANT: If createdWaybill from handleCreate is the new number, 
            // use that for printing. If handleCreate returned a string, it's our new number.
            printData.tracking_no = createdWaybill;
            await printShipmentLabel(printData);
        }
    };

    const duplicateShipment = waybillNo ? shipments?.find(s => s.tracking_no === waybillNo) : null;
    const isReprintMode = !!waybillNo && !!duplicateShipment;

    // ── 重打模式：回填收货人信息 ──────────────────────────────────
    // 当扫到已存在的运单时，从 DB 记录中恢复收货人 state，
    // 确保 ContactPicker 显示正确内容，且多次打印不丢失收货人信息。
    useEffect(() => {
        if (duplicateShipment) {
            // 进入重打模式 → 从存档记录回填收货人
            if (duplicateShipment.receiver_name) {
                setReceiver({
                    name: duplicateShipment.receiver_name,
                    phone: duplicateShipment.receiver_phone || '',
                    address: duplicateShipment.receiver_address || '',
                    city: duplicateShipment.receiver_city || '',
                });
            }
            // 同步其他字段（发货人、运输方式等），方便 update 模式修改
            if (duplicateShipment.shipper_name) setShipperName(duplicateShipment.shipper_name);
        } else if (!waybillNo) {
            // 清空运单号 → 重置收货人（回到地址簿默认或空）
            setReceiver(null);
        }
    }, [duplicateShipment?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleReprint = async () => {
        if (!duplicateShipment) return;

        try {
            if (reprintMode === 'update') {
                // Update mode: User wants to re-weigh/re-measure after adding packaging
                const updates: Partial<Shipment> = {
                    weight: parseFloat(weight) || (duplicateShipment.weight || 0),
                    length: length ? parseFloat(length) : (duplicateShipment.length || undefined),
                    width: width ? parseFloat(width) : (duplicateShipment.width || undefined),
                    height: height ? parseFloat(height) : (duplicateShipment.height || undefined),
                    shipper_name: shipperName || (duplicateShipment.shipper_name || undefined),
                    transport_mode: transportMode,
                    item_category: itemCategory || (duplicateShipment.item_category || ''),
                    // 收货人也同步更新（用户可能在重打前修改了收货人）
                    receiver_name: receiver?.name || duplicateShipment.receiver_name || undefined,
                    receiver_phone: receiver?.phone || duplicateShipment.receiver_phone || undefined,
                    receiver_address: receiver?.address || duplicateShipment.receiver_address || undefined,
                    receiver_city: receiver?.city || duplicateShipment.receiver_city || undefined,
                };

                await updateShipment.mutateAsync({
                    id: duplicateShipment.id,
                    updates
                });

                // Print label with NEW data (including any receiver changes)
                await printShipmentLabel({
                    tracking_no: duplicateShipment.tracking_no,
                    weight: updates.weight || 0,
                    length: updates.length,
                    width: updates.width,
                    height: updates.height,
                    shipper_name: updates.shipper_name,
                    receiver_name: updates.receiver_name,
                    receiver_phone: updates.receiver_phone,
                    receiver_address: [updates.receiver_city, updates.receiver_address].filter(Boolean).join(' ') || undefined,
                    batch_no: activeBatch?.batch_no,
                });
                toast.success(`数据已更新并重印: ${duplicateShipment.tracking_no}`);
            } else {
                // Copy mode: Fast reprint - use current receiver state (UI) as source of truth,
                // fall back to DB record if no picker selection.
                await printShipmentLabel({
                    tracking_no: duplicateShipment.tracking_no,
                    weight: duplicateShipment.weight || 0,
                    length: duplicateShipment.length || undefined,
                    width: duplicateShipment.width || undefined,
                    height: duplicateShipment.height || undefined,
                    shipper_name: shipperName || duplicateShipment.shipper_name || undefined,
                    receiver_name: receiver?.name || duplicateShipment.receiver_name || undefined,
                    receiver_phone: receiver?.phone || duplicateShipment.receiver_phone || undefined,
                    receiver_address: [
                        receiver?.city || duplicateShipment.receiver_city,
                        receiver?.address || duplicateShipment.receiver_address
                    ].filter(Boolean).join(' ') || undefined,
                    batch_no: activeBatch?.batch_no,
                });
                toast.success(`重印指令已发出: ${duplicateShipment.tracking_no}`);
            }

            // Clear only waybill after re-printing to allow next scan
            setWaybillNo('');
        } catch (err: any) {
            toast.error('重印失败: ' + err.message);
        }
    };

    const handleDelete = async (id: string) => {
        await removeShipment.mutateAsync({ id });
    };

    const handleEditClick = (shipment: Shipment) => {
        setEditingShipment(shipment);
        setEditWaybill(shipment.tracking_no || '');
        setEditWeight((shipment.weight || 0).toString());
        setEditLength((shipment.length || 0).toString());
        setEditWidth((shipment.width || 0).toString());
        setEditHeight((shipment.height || 0).toString());
        setEditHeight((shipment.height || 0).toString());
        setEditShipperName(shipment.shipper_name || '');
        setEditTransportMode(shipment.transport_mode || 1);
        setEditItemCategory(shipment.item_category || '');
    };

    const handleUpdateShipment = async () => {
        if (!editingShipment) return;
        try {
            await updateShipment.mutateAsync({
                id: editingShipment.id,
                updates: {
                    tracking_no: editWaybill,
                    weight: parseFloat(editWeight) || 0,
                    length: editLength ? parseFloat(editLength) : null,
                    width: editWidth ? parseFloat(editWidth) : null,
                    height: editHeight ? parseFloat(editHeight) : null,
                    shipper_name: editShipperName || null,
                    transport_mode: editTransportMode,
                    item_category: editItemCategory || null
                }
            });
            setEditingShipment(null);
            toast.success('更新成功');
        } catch (err: any) {
            toast.error('更新失败: ' + err.message);
        }
    };

    const confirmDelete = (id: string) => {
        setDeleteShipmentId(id);
        setIsDeleteConfirmOpen(true);
    };

    const executeDelete = async () => {
        if (!deleteShipmentId) return;
        try {
            await removeShipment.mutateAsync({ id: deleteShipmentId });
            setIsDeleteConfirmOpen(false);
            setDeleteShipmentId(null);
        } catch (err: any) {
            // Error handled by hook
        }
    };

    const volumetricWeight = (parseFloat(length || '0') * parseFloat(width || '0') * parseFloat(height || '0')) / 6000;
    const chargeableWeight = Math.max(parseFloat(weight || '0'), volumetricWeight);

    // Summary calculations for modal
    const totalCount = shipments?.length || 0;
    const totalChargeableWeight = shipments?.reduce((sum, s) => {
        const v = ((s.length || 0) * (s.width || 0) * (s.height || 0)) / 6000;
        return sum + Math.max(s.weight || 0, v);
    }, 0) || 0;

    const activeBatch = batches?.find(b => b.id === activeBatchId);

    const handleFinalize = async () => {
        if (!activeBatchId) return;
        try {
            await updateBatchStatus.mutateAsync({ id: activeBatchId, status: 'sender_sealed' });
            setIsConfirmModalOpen(false);
            navigate('/batch-list');
            toast.success('批次已封箱并发出');
        } catch (e: any) {
            // Error handled by hook toast
        }
    };

    return (
        <div className="bg-background-light dark:bg-background-dark font-display text-gray-900 dark:text-gray-100 min-h-screen flex flex-col relative overflow-hidden">
            <CargoCreateHeader
                batchNo={activeBatch?.batch_no}
                totalCount={totalCount}
                totalWeight={shipments?.reduce((sum, s) => sum + (s.weight || 0), 0) || 0}
            />

            <main className="flex-1 overflow-y-auto custom-scrollbar p-4 flex flex-col gap-5 pb-40">
                <RecentRecords
                    shipments={shipments}
                    latestShipment={latestShipment}
                    handleDelete={handleDelete}
                    handlePrint={(s) => printShipmentLabel({
                        tracking_no: s.tracking_no,
                        weight: s.weight || 0,
                        length: s.length || undefined,
                        width: s.width || undefined,
                        height: s.height || undefined,
                        shipper_name: s.shipper_name || undefined,
                        receiver_name: s.receiver_name || undefined,
                        receiver_phone: s.receiver_phone || undefined,
                        receiver_address: [s.receiver_city, s.receiver_address].filter(Boolean).join(' ') || undefined,
                        batch_no: activeBatch?.batch_no,
                    })}
                />

                <CargoCreateForm
                    waybillNo={waybillNo}
                    setWaybillNo={setWaybillNo}
                    weight={weight}
                    setWeight={setWeight}
                    length={length}
                    setLength={setLength}
                    width={width}
                    setWidth={setWidth}
                    height={height}
                    setHeight={setHeight}
                    shipperName={shipperName}
                    setShipperName={setShipperName}
                    receiver={receiver}
                    setReceiver={setReceiver}
                    transportMode={transportMode}
                    setTransportMode={setTransportMode}
                    itemCategory={itemCategory}
                    setItemCategory={setItemCategory}
                    volumetricWeight={volumetricWeight}
                    chargeableWeight={chargeableWeight}
                    isReprintMode={isReprintMode}
                    archiveShipment={duplicateShipment}
                />
            </main>

            <CargoCreateFooter
                handleCreate={handleCreate}
                handlePrint={handlePrint}
                handleReprint={handleReprint}
                isCreating={addShipment.isPending}
                isPrinting={isLabelPrinting}
                isReprintMode={isReprintMode}
                duplicateTrackingNo={duplicateShipment?.tracking_no}
                openConfirmModal={() => setIsConfirmModalOpen(true)}
            />

            <FinalizeModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                activeBatch={activeBatch}
                totalCount={totalCount}
                totalChargeableWeight={totalChargeableWeight}
                shipments={shipments}
                handleEditClick={handleEditClick}
                confirmDelete={confirmDelete}
                handlePrint={(s) => printShipmentLabel({
                    tracking_no: s.tracking_no,
                    weight: s.weight || 0,
                    length: s.length || undefined,
                    width: s.width || undefined,
                    height: s.height || undefined,
                    shipper_name: s.shipper_name || undefined,
                    batch_no: activeBatch?.batch_no,
                })}
                handleFinalize={handleFinalize}
                isFinalizing={updateBatchStatus.isPending}
            />

            <EditShipmentModal
                editingShipment={editingShipment}
                onClose={() => setEditingShipment(null)}
                waybill={editWaybill}
                setWaybill={setEditWaybill}
                weight={editWeight}
                setWeight={setEditWeight}
                length={editLength}
                setLength={setEditLength}
                width={editWidth}
                setWidth={setEditWidth}
                height={editHeight}
                setHeight={setEditHeight}
                shipperName={editShipperName}
                setShipperName={setEditShipperName}
                transportMode={editTransportMode}
                setTransportMode={setEditTransportMode}
                itemCategory={editItemCategory}
                setItemCategory={setEditItemCategory}
                handleUpdateShipment={handleUpdateShipment}
            />

            <DeleteConfirmDialog
                isOpen={isDeleteConfirmOpen}
                onConfirm={executeDelete}
                onCancel={() => setIsDeleteConfirmOpen(false)}
            />

            {/* Generation Choice Modal */}
            {isGenChoiceModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in text-left">
                    <div className="bg-[#1e293b] w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border border-white/10 animate-slide-up">
                        <div className="p-6">
                            <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                                <span className="material-icons text-primary">auto_fix_high</span>
                                {t('cargo.gen_choice_title', '选择生成方式')}
                            </h3>
                            <p className="text-slate-400 text-sm mb-6">
                                {t('cargo.gen_choice_desc', '请选择您想要生成的运单号类型：')}
                            </p>

                            <div className="space-y-3">
                                <button
                                    onClick={() => handleWaybillGen('SEQUENCE')}
                                    className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all active:scale-[0.98] group"
                                >
                                    <div className="text-left">
                                        <div className="text-white font-medium group-hover:text-primary transition-colors">{t('cargo.gen_choice_sequence', '使用规则单号')}</div>
                                        <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider">{t('settings.sequence_gen_mode_sequence', '前缀+日期+流水')}</div>
                                    </div>
                                    <span className="material-icons text-slate-500 group-hover:text-primary">chevron_right</span>
                                </button>

                                <button
                                    onClick={() => handleWaybillGen('RANDOM_ALPHA')}
                                    className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all active:scale-[0.98] group"
                                >
                                    <div className="text-left">
                                        <div className="text-white font-medium group-hover:text-blue-400 transition-colors">{t('cargo.gen_choice_random', '生成随机 8 位大写号')}</div>
                                        <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider">ABCDEFGH ({t('common.random', '随机')})</div>
                                    </div>
                                    <span className="material-icons text-slate-500 group-hover:text-blue-400">chevron_right</span>
                                </button>
                            </div>

                            <button
                                onClick={() => setIsGenChoiceModalOpen(false)}
                                className="w-full mt-6 py-3 text-slate-500 hover:text-white transition-colors text-sm font-bold uppercase tracking-widest"
                            >
                                {t('common.cancel', '取消')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CargoCreate;
